import { SearchableOtherBets } from 'components/OtherBets/SearchableOtherBets';
import { OtherBets } from 'components/OtherBets/OtherBets';
import { useHeldOddChange } from 'hooks/useHeldOddChange';
import { RedCards } from 'components/RedCards/RedCards';
import { LiveEvent as LiveEventDto, TennisLiveEvent } from 'types/LiveEvent';
import { SportIDs } from 'types/enums';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import styles from './LiveEvent.module.css';
import { LIVE_FULL_TIME_BET_MAP } from 'config/constants';
import {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { LiveBettingContext } from 'context/liveBettingContext';
import { useTranslation } from 'react-i18next';
import { liveClock } from 'utils/sportEvent.utils';
import { SlipContext } from 'context/slipContext';
import { getFullTimeOdds } from 'helpers/matchHelper';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { translateBetMarketName } from 'helpers/betI18n';
import type { TFunction } from 'i18next';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { usePlayerLayout } from 'lib/playerLayout';
import { ModernLiveSheet } from './ModernLiveSheet';
import { ROW_ATTR } from 'lib/rowAnchor';
import { VipIcon } from 'components/mobile/VipChrome/VipIcons';
import { LiveMarket, livePickLabel } from '../liveMarkets';
import { LiveEventOdd } from 'types/LiveEvent';
import {
  isSuspendedOdd,
  SuspendedLock,
} from 'components/SuspendedLock/SuspendedLock';

interface Props {
  event: LiveEventDto;
  isFavorite?: boolean;
  /** Modern: the odds the row shows instead of 1 X 2 (double chance...). */
  market?: LiveMarket;
}

export const LiveEvent = ({
  event,
  isFavorite: inFavoritesList = false,
  market,
}: Props) => {
  const [prevFullTimeData, setPrevFullTimeData] = useState<any>(null);
  const [showMoreBets, setShowMoreBets] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  // Modern: a tap opens the match in a sheet instead of the markets below.
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  const vip = layout === 'vip';
  const [sheetOpen, setSheetOpen] = useState(false);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const { addSlipEntry, removeSlipEntry, entryExist } = useContext(SlipContext);
  const {
    events,
    getLastOddsForEvent,
    addFavorite,
    removeFavorite,
    isFavorite: isFavoriteEvent,
  } = useContext(LiveBettingContext);
  // A favourite stays in its sport's list too, its star filled there as well.
  const isFavorite = inFavoritesList || isFavoriteEvent(event);

  const { homeWin, draw, awayWin } = getFullTimeOdds(event);

  const { t } = useTranslation();

  useEffect(() => {
    const data = getLastOddsForEvent(event);
    if (data) {
      setPrevFullTimeData(data);
    }
  }, [events, event.fi, event.sportId]); // eslint-disable-line react-hooks/exhaustive-deps -- getLastOddsForEvent(event)

  // Read off the slip on every render, not kept in the row: a row drawn
  // again (a reorder, a list replaced) started unselected until the slip
  // next changed, and the draw / away checks read the home pick's state, so
  // a second pick on a match lit up or not by chance (owner, 2026-11-04:
  // "poplavi, promijeni pa vrati").
  const isPicked = (odd?: LiveEventOdd) =>
    !!odd && entryExist(event, event.fullTimeBet, odd);
  const homeWinSelected = isPicked(homeWin);
  const drawSelected = isPicked(draw);
  const awayWinSelected = isPicked(awayWin);

  const handleFavoriteClick = (e: MouseEvent) => {
    // The row opens the other markets; the star only marks the favourite.
    e.stopPropagation();
    if (!isFavorite) {
      addFavorite(event);
    } else {
      removeFavorite(event);
    }
  };

  const handle1x2Click = (bet: '1' | 'Draw' | '2') => {
    const odd = event.fullTimeBet.odds.find((x) => x.name === bet);
    if (!odd) return;

    if (
      (homeWinSelected && bet === '1') ||
      (drawSelected && bet === 'Draw') ||
      (awayWinSelected && bet === '2')
    ) {
      removeSlipEntry(event, event.fullTimeBet, odd);
    } else {
      addSlipEntry(event, event.fullTimeBet, odd, true);
    }
  };

  const getDataChangedCssValue = () => {
    const labels = {
      homeWin: '',
      draw: '',
      awayWin: '',
    };
    if (prevFullTimeData) {
      const prevFullTimeOdds = getFullTimeOdds(prevFullTimeData);

      if (
        homeWin?.odds != null &&
        prevFullTimeOdds.homeWin?.odds != null &&
        prevFullTimeOdds.homeWin.odds > homeWin.odds
      ) {
        labels.homeWin = 'lower-value';
      } else if (
        homeWin?.odds != null &&
        prevFullTimeOdds.homeWin?.odds != null &&
        prevFullTimeOdds.homeWin.odds < homeWin.odds
      ) {
        labels.homeWin = 'higher-value';
      }

      if (
        draw?.odds != null &&
        prevFullTimeOdds.draw?.odds != null &&
        prevFullTimeOdds.draw.odds > draw.odds
      ) {
        labels.draw = 'lower-value';
      } else if (
        draw?.odds != null &&
        prevFullTimeOdds.draw?.odds != null &&
        prevFullTimeOdds.draw.odds < draw.odds
      ) {
        labels.draw = 'higher-value';
      }

      if (
        awayWin?.odds != null &&
        prevFullTimeOdds.awayWin?.odds != null &&
        prevFullTimeOdds.awayWin.odds > awayWin.odds
      ) {
        labels.awayWin = 'lower-value';
      } else if (
        awayWin?.odds != null &&
        prevFullTimeOdds.awayWin?.odds != null &&
        prevFullTimeOdds.awayWin.odds < awayWin.odds
      ) {
        labels.awayWin = 'higher-value';
      }
    }

    return labels;
  };
  const changedLabels = useMemo(
    () => getDataChangedCssValue(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prevFullTimeData, homeWin?.odds, draw?.odds, awayWin?.odds]
  );

  const homeMark = useHeldOddChange(changedLabels.homeWin, homeWin?.odds);
  const drawMark = useHeldOddChange(changedLabels.draw, draw?.odds);
  const awayMark = useHeldOddChange(changedLabels.awayWin, awayWin?.odds);

  // Modern market chips: another market in place of the 1 X 2.
  const otherMarket = market && market.key !== 'full' ? market : undefined;
  // Without a winner (a one-sided basketball or handball game keeps only
  // its lines), the row offers the handicap, as forzza.com does, rather
  // than "no offer".
  const handicapFallback =
    !otherMarket && !(homeWin && awayWin) ? spreadOf(event) : null;
  const otherBet = otherMarket?.pick?.(event) ?? handicapFallback;
  const totalsLine =
    otherBet?.odds.find((x) => !!x.handicap)?.handicap?.replace(/^[+-]/, '') ??
    '';

  const betsToShow = event.bets.filter(
    (x) => x.name !== LIVE_FULL_TIME_BET_MAP.get(event.sportId)
  );

  const odds = (
    <>
      {otherMarket?.feedName ? (
        otherBet && otherBet.odds.length > 0 ? (
          // Any other market of the feed: shown whole, as in the details.
          <div className={styles.fullMarket}>
            <OtherBets bets={[otherBet]} match={event} isLive noTopBorder />
          </div>
        ) : (
          <div className={styles.noOfferMessage}>
            {t('no_bet_offer_message')}
          </div>
        )
      ) : otherMarket || handicapFallback ? (
        otherBet && otherBet.odds.length > 0 ? (
          <>
            <div className={styles.threeWayLabel}>
              {handicapFallback
                ? t('handicap_label')
                : otherMarket?.totals
                  ? `${t(otherMarket.label)} ${totalsLine}`
                  : translateBetMarketName(otherBet, t)}
            </div>
            <div className={styles.betsWrapper}>
              {otherBet.odds.map((odd) => {
                const selected = entryExist(event, otherBet, odd);
                const locked = isSuspendedOdd(odd);
                return (
                  <div
                    key={odd.custom_id}
                    className={selected ? styles.betSelected : ''}
                    data-suspended={locked || undefined}
                    onClick={() =>
                      selected
                        ? removeSlipEntry(event, otherBet, odd)
                        : !locked && addSlipEntry(event, otherBet, odd, true)
                    }
                  >
                    <span>
                      {handicapFallback
                        ? `${odd === otherBet.odds[0] ? '1' : '2'} (${(odd.handicap ?? '').trim()})`
                        : livePickLabel(event, odd, !!otherMarket?.totals, t)}
                    </span>
                    <span>
                      {locked ? <SuspendedLock /> : odd.odds.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className={styles.noOfferMessage}>
            {t('no_bet_offer_message')}
          </div>
        )
      ) : homeWin && awayWin ? (
        <>
          <div className={styles.threeWayLabel}>{fullTimeLabel(event, t)}</div>
          <div className={styles.betsWrapper}>
            <div
              key={`home-${homeMark.key}`}
              data-changed={isSuspendedOdd(homeWin) ? '' : homeMark.label}
              data-suspended={isSuspendedOdd(homeWin) || undefined}
              className={homeWinSelected ? styles.betSelected : ''}
              onClick={() => {
                if (isSuspendedOdd(homeWin) && !homeWinSelected) return;
                handle1x2Click('1');
              }}
            >
              <span>{t('bet_col_1x2_home')}</span>
              <span>
                {isSuspendedOdd(homeWin) ? (
                  <SuspendedLock />
                ) : (
                  homeWin.odds.toFixed(2)
                )}
              </span>
            </div>
            {draw && (
              <div
                key={`draw-${drawMark.key}`}
                data-changed={isSuspendedOdd(draw) ? '' : drawMark.label}
                data-suspended={isSuspendedOdd(draw) || undefined}
                className={drawSelected ? styles.betSelected : ''}
                onClick={() => {
                  if (isSuspendedOdd(draw) && !drawSelected) return;
                  handle1x2Click('Draw');
                }}
              >
                <span>{t('bet_col_1x2_draw')}</span>
                <span>
                  {isSuspendedOdd(draw) ? (
                    <SuspendedLock />
                  ) : (
                    draw.odds.toFixed(2)
                  )}
                </span>
              </div>
            )}
            <div
              key={`away-${awayMark.key}`}
              data-changed={isSuspendedOdd(awayWin) ? '' : awayMark.label}
              data-suspended={isSuspendedOdd(awayWin) || undefined}
              className={awayWinSelected ? styles.betSelected : ''}
              onClick={() => {
                if (isSuspendedOdd(awayWin) && !awayWinSelected) return;
                handle1x2Click('2');
              }}
            >
              <span>{t('bet_col_1x2_away')}</span>
              <span>
                {isSuspendedOdd(awayWin) ? (
                  <SuspendedLock />
                ) : (
                  awayWin.odds.toFixed(2)
                )}
              </span>
            </div>
          </div>
        </>
      ) : (
        <NoOdds vip={vip} event={event} />
      )}
    </>
  );

  const insights = showInsights && (
    <MatchInsightsModal
      fi={event.fi}
      onClose={() => setShowInsights(false)}
      liveOdds={<SearchableOtherBets match={event} bets={event.bets} />}
      heroOdds={
        vip ? <div className={styles.vipHeroOdds}>{odds}</div> : undefined
      }
    />
  );

  // VIP: minute and league over the line; teams, score and 1 X 2 on it.
  // The row the list keeps in place (lib/rowAnchor); a favourite is listed
  // twice, on top as well.
  const rowMark = {
    [ROW_ATTR]: `${inFavoritesList ? 'fav:' : ''}${event.fi}`,
  };

  // Any market other than 1 X 2 needs the width, so it goes under the line.
  if (vip) {
    const oddsBeside = !otherMarket && !handicapFallback;
    return (
      <div className={styles.vipRow} {...rowMark}>
        {insights}
        <div
          className={styles.vipHead}
          onClick={() => setShowMoreBets(!showMoreBets)}
        >
          <i className={styles.vipDot} />
          <b>{liveClock(event, t)}</b>
          <span>{event.league}</span>
          <button
            type="button"
            className={styles.vipTool}
            aria-label="Favorite"
            aria-pressed={isFavorite}
            data-on={isFavorite}
            onClick={handleFavoriteClick}
          >
            <VipIcon name="star" size={19} />
          </button>
          <button
            type="button"
            className={styles.vipTool}
            aria-label={t('match_statistics_label')}
            onClick={(e) => {
              e.stopPropagation();
              setShowInsights(true);
            }}
          >
            <VipIcon name="chart" size={19} />
          </button>
          {betsToShow.length > 0 && (
            <em>
              +{betsToShow.length}
              <VipIcon name="chevron" size={15} />
            </em>
          )}
        </div>
        <div
          className={styles.vipLine}
          onClick={() => setShowMoreBets(!showMoreBets)}
        >
          <div className={styles.teams}>
            <span>
              <span>{event.home.name}</span>
              <RedCards
                count={event.homeRedCards}
                yellow={event.liveCounts?.yellow?.[0]}
              />
            </span>
            <span>
              <span>{event.away.name}</span>
              <RedCards
                count={event.awayRedCards}
                yellow={event.liveCounts?.yellow?.[1]}
              />
            </span>
          </div>
          {event.sportId === SportIDs.TENNIS ? (
            <div className={styles.results}>
              <TennisScore event={event as TennisLiveEvent} />
            </div>
          ) : (
            <span className={styles.vipScore}>
              <b>{event.homeGoals}</b>
              <b>{event.awayGoals}</b>
            </span>
          )}
          {oddsBeside && <div onClick={(e) => e.stopPropagation()}>{odds}</div>}
        </div>
        {!oddsBeside && <div className={styles.vipUnder}>{odds}</div>}
        {showMoreBets && betsToShow.length > 0 && (
          <SearchableOtherBets match={event} bets={betsToShow} />
        )}
      </div>
    );
  }

  return (
    <>
      {insights}
      {sheetOpen && <ModernLiveSheet event={event} close={closeSheet} />}
      <div
        className={styles.wrapper}
        {...rowMark}
        onClick={() =>
          modern ? setSheetOpen(true) : setShowMoreBets(!showMoreBets)
        }
      >
        <div className={styles.league}>
          <span className={styles.time}>{liveClock(event, t)}</span>
          {event.league}
        </div>
        <div className={styles.matchData}>
          <button
            type="button"
            className={styles.favorite}
            aria-label="Favorite"
            aria-pressed={isFavorite}
            onClick={handleFavoriteClick}
          >
            <ThemeIcon
              icon="star"
              active={isFavorite}
              alt=""
              variant="inline"
            />
          </button>
          <div className={styles.teams}>
            <span>
              {event.home.name}
              <RedCards
                count={event.homeRedCards}
                yellow={event.liveCounts?.yellow?.[0]}
              />
            </span>
            <span>
              {event.away.name}
              <RedCards
                count={event.awayRedCards}
                yellow={event.liveCounts?.yellow?.[1]}
              />
            </span>
          </div>
          {!modern && (
            <button
              type="button"
              className={styles.statsButton}
              aria-label={t('match_statistics_label')}
              onClick={(e) => {
                e.stopPropagation();
                setShowInsights(true);
              }}
            >
              📊
            </button>
          )}
          <div className={styles.results}>
            {event.sportId === SportIDs.TENNIS ? (
              <TennisScore event={event as TennisLiveEvent} />
            ) : (
              <>
                {event.homeGoals}:{event.awayGoals}
              </>
            )}
            {betsToShow.length > 0 && (
              <div className={styles.moreBetsWrapper}>
                +{betsToShow.length}
                <div className={styles.moreBetsIcon} />
              </div>
            )}
          </div>
        </div>
      </div>
      {odds}
      {!modern && showMoreBets && betsToShow.length > 0 && (
        <div>
          <SearchableOtherBets match={event} bets={betsToShow} />
        </div>
      )}
    </>
  );
};

/** No 1 X 2 right now: the row's picks locked, in every layout (owner,
 *  2026-11-03: a lock as on VIP, not a sentence). A draw only where the sport
 *  has one. */
const THREE_WAY_SPORTS: number[] = [
  SportIDs.SOCCER,
  SportIDs.ICE_HOCKEY,
  SportIDs.HANDBALL,
];
const NoOdds = ({ vip, event }: { vip: boolean; event: LiveEventDto }) => {
  const { t } = useTranslation();
  const cols = THREE_WAY_SPORTS.includes(event.sportId)
    ? ['bet_col_1x2_home', 'bet_col_1x2_draw', 'bet_col_1x2_away']
    : ['bet_col_1x2_home', 'bet_col_1x2_away'];
  const tiles = (
    <div className={styles.betsWrapper} aria-label={t('no_bet_offer_message')}>
      {cols.map((x) => (
        <div key={x} data-suspended>
          <span>{t(x)}</span>
          <span>
            <SuspendedLock />
          </span>
        </div>
      ))}
    </div>
  );
  if (vip) return tiles;
  return (
    <>
      <div className={styles.threeWayLabel}>{fullTimeLabel(event, t)}</div>
      {tiles}
    </>
  );
};

/** The row's own market: "Full Time" was shown untranslated. */
const fullTimeLabel = (event: LiveEventDto, t: TFunction) =>
  event.sportId === SportIDs.SOCCER
    ? translateBetMarketName({ name: 'Full Time Result', odds: [] }, t)
    : t('bet_odd_winner');

/**
 * Tennis in play, as forzza.com shows it: who serves, the points of the game
 * being played, games in the current set and sets, one row per player.
 */
const TennisScore = ({ event }: { event: TennisLiveEvent }) => {
  const split = (x?: string) => (x ?? '').split(':');
  const [homePts, awayPts] = split(event.currentGameResult);
  const [homeGames, awayGames] = split(event.currentSetResult);
  const row = (
    serving: boolean,
    pts?: string,
    games?: string,
    sets?: number
  ) => (
    <span className={styles.tennisRow}>
      <span className={styles.tennisServe} data-on={serving}>
        {serving && <SportIcon sportId={SportIDs.TENNIS} alt="" />}
      </span>
      {pts ? <span className={styles.tennisPoints}>{pts}</span> : <span />}
      <span className={styles.tennisGames}>{games ?? ''}</span>
      <span className={styles.tennisSets}>{sets ?? 0}</span>
    </span>
  );
  return (
    <span className={styles.tennisScore}>
      {row(event.currentServe === 'home', homePts, homeGames, event.homeGoals)}
      {row(event.currentServe === 'away', awayPts, awayGames, event.awayGoals)}
    </span>
  );
};

const SIGNED_LINE = /^[+-]\d+(\.\d+)?$/;

/**
 * The full-game handicap of an event in play, home side first: basketball
 * "Game Lines" NA "Spread" with the team in name, handball NA team with the
 * line in HA. Totals have an unsigned line and are left out.
 */
const spreadOf = (event: LiveEventDto) => {
  const bet = event.bets.find((b) => b.name === 'Game Lines');
  if (!bet) return null;
  const lines = bet.odds.filter(
    (o) =>
      SIGNED_LINE.test((o.handicap ?? '').trim()) &&
      !/^[OU] /.test((o.header ?? '').trim())
  );
  // Handball lists several lines; the first pair is the main one.
  const isHome = (o: LiveEventOdd) =>
    o.name === event.home.name || o.name3 === event.home.name;
  const lineOf = (o: LiveEventOdd) => Number((o.handicap ?? '').trim());
  const home = lines.find(isHome) ?? lines[0];
  const away = lines.find(
    (o) => o !== home && !isHome(o) && lineOf(o) === -lineOf(home)
  );
  if (!home || !away) return null;
  const odds = [home, away];
  return { ...bet, odds };
};
